import { observe, ObservableMap, isComputed } from 'mobx';
import CartStore from './cart';
import PendingCartStore from './pending-cart';
import FastEntryStore from './fastentry';
import CheckoutStore from './checkout';
import ItemStore from './item';
import ItemlistStore from './itemlist';
import SearchStore from './search';
import ShoppinglistStore from './shoppinglist';
import OrderApprovalStore from './orderapproval';
import { fetchStockinfo, fetchPrices, filterEventHandler } from '../itemlist';


// Organize stores with jquery
$.stores = {
  cart: new CartStore(),
  pendingCart: new PendingCartStore(),
  fastEntry: new FastEntryStore(),
  checkout: new CheckoutStore(),
  item: new ItemStore(),
  itemlist: new ItemlistStore(),
  search: new SearchStore(),
  shoppinglist: new ShoppinglistStore(),
  orderapproval: new OrderApprovalStore(),
};

$.isRefocusing = false;

// Add jquery destroyed event
$.event.special.destroyed = {
  remove: (o) => {
    if (o.handler) {
      o.handler();
    }
  },
};

// Add data-bind
$(document).ready(() => {
  const handlers = new Map();

  const attachDataObserve = ($elem, invokeImmediately = false) => {
    const disposers = [];

    // Attach handler for each existing data-bind
    $.each(handlers, (tag, handler) => {
      const dataValue = $elem.attr(tag);
      if (dataValue) {
        const commands = dataValue.split('|');
        commands.reverse();

        commands.forEach((command) => {
          const store = command.split(':', 1)[0];
          const values = command.slice(store.length + 1).split(',');
          const params = (values.length > 1) ? values.splice(1) : [];
          let property = values[0];

          // Check if observing nested map
          if (property.includes('.')) {
            const steps = property.split('.');
            let parent = $.stores[store];
            steps.slice(0, -1).forEach((step, i) => {
              if (parent instanceof ObservableMap) {
                // check if step exists, if not may be an item_id with "." inside
                // so check if combined next steps match
                let tmpParent = parent.get(step);
                let j = i + 1;
                let key = step;
                while (tmpParent === undefined && j < steps.length) {
                  key = `${key}.${steps[j]}`;
                  tmpParent = parent.get(key);
                  j += 1;
                }
                if (tmpParent !== undefined) {
                  parent = tmpParent;
                }
              } else {
                parent = parent[step];
              }
            });

            // Attach observer for given store and nested property/map
            let propertyObj;
            property = steps[steps.length - 1];
            if (parent instanceof ObservableMap) {
              propertyObj = parent.get(property);
            } else {
              propertyObj = parent[property];
            }

            // If property is computed we have to attach the observer directly to the
            // computed property, else to the parent
            let observable = parent;
            if (isComputed(propertyObj)) {
              observable = propertyObj;
            }

            disposers.push(observable.observe((change) => {
              if (change.name === property || isComputed(change.object)) {
                handler($elem, change.newValue, params);
              }
            }));

            if (invokeImmediately) {
              if (isComputed(propertyObj)) {
                handler($elem, propertyObj.get(), params);
              } else {
                handler($elem, propertyObj, params);
              }
            }
          } else {
            // Attach observer for given store and property
            disposers.push(observe($.stores[store], property, (change) => {
              handler($elem, change.newValue, params);
            }));
          }
        });
      }
    });

    // Remove observer, when node is removed (avoid memory leak)
    if (disposers) {
      $elem.on('destroyed', () => {
        for (let i = 0; i < disposers.length; i += 1) {
          disposers[i]();
        }
      });
    }
  };

  $.stores.initElement = ($elem, focusedElement = null, invokeImmediately = false) => {
    $.stores.cart.gatherCartItems($elem);
    fetchPrices();
    fetchStockinfo();

    $elem.find(`[${Object.keys(handlers).join('], [')}]`).each(function () {
      attachDataObserve($(this), invokeImmediately);
    });

    // Initialize foundation, focus
    $elem.foundation();
    $elem.autocomplete(filterEventHandler);

    let newFocusedElement;

    if (focusedElement && focusedElement.id) {
      newFocusedElement = $elem.find(`#${focusedElement.id}`).focus().get(0);
      if (newFocusedElement) {
        window.setTimeout(() => {
          newFocusedElement.focus();
        }, 0);
      }

      try {
        if (newFocusedElement && focusedElement.selectionStart !== undefined) {
          newFocusedElement.setSelectionRange(focusedElement.selectionStart, focusedElement.selectionEnd);
        }
      } catch (e) {
        // ignore errors
      }
    }

    return newFocusedElement;
  };

  // The data-bind-select will select elements content dependent
  // on the state of the given observable, which has to be true or false.
  handlers['data-bind-select'] = ($elem, newValue) => {
    if (newValue) {
      window.setTimeout(() => {
        $elem.focus().select();
      }, 1);
    }
  };

  // In case of html the dom node is completely replaced
  // with the new html content. Because of this we need to
  // reattach the observer to the new node and all nodes with
  // data tags in the new html content. We have to initialize
  // foundation and to focus focused elements.
  handlers['data-bind-html'] = ($elem, newValue) => {
    const focusedElement = document.activeElement;
    const autocompleteScrollTop = $(focusedElement).find('+ .autocomplete').scrollTop();
    const autocompleteInputValue = $(focusedElement).filter('.autocomplete-input').val();
    const $newElem = $(newValue);

    // IE FIX (IE seems to forget/mutate focusedElement selection)
    const focusedElementObj = Object();
    if (focusedElement) {
      focusedElementObj.id = focusedElement.id;
      try {
        focusedElementObj.selectionStart = focusedElement.selectionStart;
        focusedElementObj.selectionEnd = focusedElement.selectionEnd;
      } catch (e) {
        // ignore IE11 InvalidStateError
      }
    }

    let fastEntry;
    if ($elem.find('[data-fastentry-item]').length) {
      fastEntry = $elem.find('[data-fastentry-item]').get(0);
    }
    $.isRefocusing = true;
    $elem.replaceWith($newElem);
    if (fastEntry) {
      $newElem.find('[data-fastentry-item]').replaceWith(fastEntry);
    }
    attachDataObserve($newElem);
    const newFocusedElement = $.stores.initElement($newElem, focusedElementObj);
    if (newFocusedElement) {
      $(newFocusedElement).filter('.autocomplete-input').val(autocompleteInputValue).trigger('input');
      $(newFocusedElement).find('+ .autocomplete').scrollTop(autocompleteScrollTop);
    }
    $.isRefocusing = false;
  };

  // In case of html the inner html is replaced
  // with the new html content. Because of this we need to
  // reattach the observer all nodes with data tags in the new html content.
  // We have to initialize foundation and to focus focused elements.
  handlers['data-bind-innerhtml'] = ($elem, newValue) => {
    const focusedElement = document.activeElement;
    const autocompleteScrollTop = $(focusedElement).find('+ .autocomplete').scrollTop();
    const autocompleteInputValue = $(focusedElement).filter('.autocomplete-input').val();
    const $newElem = $(newValue);

    // IE FIX (IE seems to forget/mutate focusedElement selection)
    const focusedElementObj = Object();
    if (focusedElement) {
      focusedElementObj.id = focusedElement.id;
      focusedElementObj.selectionStart = focusedElement.selectionStart;
      focusedElementObj.selectionEnd = focusedElement.selectionEnd;
    }

    $.isRefocusing = true;
    $elem.html($newElem);
    const newFocusedElement = $.stores.initElement($newElem, focusedElementObj);
    if (newFocusedElement) {
      $(newFocusedElement).filter('.autocomplete-input').val(autocompleteInputValue).trigger('input');
      $(newFocusedElement).find('+ .autocomplete').scrollTop(autocompleteScrollTop);
    }
    $.isRefocusing = false;
  };

  // In case of text binding we only need to replace the content
  // of the node with the new content.
  handlers['data-bind-text'] = ($elem, newValue, params) => {
    let value = newValue;
    if (params && params.length === 2) {
      if (params[0] === 'fixed') {
        let numDecimals = parseInt(params[1], 10);
        numDecimals = Number.isNaN(numDecimals) ? 0 : numDecimals;
        value = parseFloat(newValue).toLocaleString('de-CH', {
          minimumFractionDigits: numDecimals,
          maximumFractionDigits: numDecimals,
        });
      }
    }
    if ($elem.is('input')) {
      $elem.val(value);
    } else {
      if (typeof value === 'number') {
        value = value.toLocaleString('de-CH');
      }
      $elem.text(value);
    }
  };

  // The data-bind-class will toggle the given css classes dependent
  // on the state of the given observable, which has to be true or false.
  handlers['data-bind-class'] = ($elem, newValue, params) => {
    $elem.toggleClass(params[0], newValue);
  };

  // The data-bind-prop-toggle will toggle the given property dependent
  // on the state of the given observable, which has to be true or false.
  handlers['data-bind-prop-toggle'] = ($elem, newValue, params) => {
    if (newValue) {
      $elem.attr(params[0], true);
    } else {
      $elem.removeAttr(params[0]);
    }
  };

  // The data-bind-prop-set will set the given attr dependent
  // to the value of the observable.
  handlers['data-bind-prop-set'] = ($elem, newValue, params) => {
    const attrs = params[0].split('=');
    if (newValue !== null) {
      if (attrs.length > 1) {
        $elem.attr(attrs[0], attrs[1].replace('{}', newValue));
      } else {
        $elem.attr(attrs[0], newValue);
      }
    } else {
      $elem.removeAttr(attrs[0]);
    }
  };

  // The data-bind-data will set the given data-attr dependent
  // to the value of the observable.
  handlers['data-bind-data'] = ($elem, newValue, params) => {
    if (newValue !== null) {
      $elem.attr(`data-${params[0]}`, newValue);
    } else {
      $elem.removeAttr(`data-${params[0]}`);
    }
  };

  // Initial attach
  $.stores.cart.gatherCartItems($(document));
  $(`[${Object.keys(handlers).join('], [')}]`).each(function () {
    attachDataObserve($(this));
  });
});
